import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import MediaInput from "../../components/admin/MediaInput";

const inputCls =
  "w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white";

export default function AlbumAdmin() {
  const [data, setData] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);

  useEffect(() => {
    api.get("/album").then(({ data }) => {
      setData(data);
      setTracks(data.tracks || []);
    });
  }, []);

  const loadSubs = () => api.get("/subscribers").then(({ data }) => setSubs(data || []));

  if (!data) return <div className="overline text-[#8A8A93]">Loading…</div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addTrack = () => setTracks((t) => [...t, { title: "", length: "", teaser_url: "" }]);
  const updateTrack = (idx, key, val) =>
    setTracks((t) => t.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const removeTrack = (idx) => setTracks((t) => t.filter((_, i) => i !== idx));
  const moveTrack = (idx, dir) =>
    setTracks((t) => {
      const j = idx + dir;
      if (j < 0 || j >= t.length) return t;
      const next = [...t];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data, tracks };
      const { data: out } = await api.put("/album", payload);
      setData(out);
      setTracks(out.tracks || []);
      toast.success("Album saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeSub = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/subscribers/${id}`);
    loadSubs();
  };

  const downloadCsv = () => {
    const rows = [["email", "name", "source", "created_at"]];
    subs.forEach((s) => rows.push([s.email, s.name || "", s.source || "", s.created_at || ""]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "artistry-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Album</h1>
          <p className="text-sm text-[#8A8A93] mt-2">Configure the /album landing page — title, hero, release date, tracklist, and email capture. Toggle <em>Enabled</em> when ready to go live.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="album-save"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <section className="border border-white/5 p-6 md:p-7 space-y-5">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={data.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            data-testid="album-enabled"
            className="w-4 h-4 accent-[#C9A050]"
          />
          <label className="overline">Enable the /album page publicly</label>
        </div>
        <p className="text-xs text-[#8A8A93]">While disabled, visitors to /album see a soft "coming soon" placeholder.</p>
      </section>

      <SectionCard title="1. Hero">
        <Field label="Overline / tagline">
          <input value={data.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={inputCls} placeholder="A body of work worth remembering." />
        </Field>
        <Field label="Album title">
          <input value={data.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="The Album" />
        </Field>
        <Field label="Subtitle / lead paragraph" hint="Shown below the album title.">
          <textarea rows={4} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Cover image (base layer of the hero)">
          <MediaInput value={data.cover_image || ""} onChange={(v) => set("cover_image", v)} accept="image" folder="album" placeholder="Cover image URL" testid="album-cover" compact />
        </Field>
        <Field label="Hero video (optional — Vimeo URL or MP4)" hint="Plays muted over the cover image as an atmospheric layer.">
          <MediaInput value={data.hero_video_url || ""} onChange={(v) => set("hero_video_url", v)} accept="video" folder="album" placeholder="Vimeo URL or MP4" testid="album-hero-video" compact />
        </Field>
        <Field label="Release date & time" hint="ISO format works best (YYYY-MM-DDTHH:MM). Powers the live countdown. Leave blank to hide the countdown.">
          <input type="datetime-local" value={(data.release_date || "").slice(0, 16)} onChange={(e) => set("release_date", e.target.value)} className={inputCls} data-testid="album-release-date" />
        </Field>
      </SectionCard>

      <SectionCard title="2. Email capture">
        <Field label="Heading">
          <input value={data.email_capture_heading || ""} onChange={(e) => set("email_capture_heading", e.target.value)} className={inputCls} placeholder="Be the first to hear it." />
        </Field>
        <Field label="Note under the heading" hint="Short reassurance about spam / frequency.">
          <textarea rows={3} value={data.email_capture_note || ""} onChange={(e) => set("email_capture_note", e.target.value)} className={inputCls} />
        </Field>
      </SectionCard>

      <SectionCard title="3. Tracklist">
        <div className="space-y-3">
          {tracks.length === 0 && (
            <p className="text-xs text-[#8A8A93] italic py-2">No tracks yet.</p>
          )}
          {tracks.map((t, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={`track-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="overline text-[#C9A050]">Track · {idx + 1}</span>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => moveTrack(idx, -1)} className="overline text-[#8A8A93] hover:text-white">↑</button>
                  <button type="button" onClick={() => moveTrack(idx, 1)} className="overline text-[#8A8A93] hover:text-white">↓</button>
                  <button type="button" onClick={() => removeTrack(idx)} data-testid={`track-remove-${idx}`} className="overline text-[#8A8A93] hover:text-red-400">Remove</button>
                </div>
              </div>
              <input placeholder="Track title" value={t.title || ""} onChange={(e) => updateTrack(idx, "title", e.target.value)} data-testid={`track-title-${idx}`} className={inputCls} />
              <input placeholder="Length (e.g. 3:42)" value={t.length || ""} onChange={(e) => updateTrack(idx, "length", e.target.value)} data-testid={`track-length-${idx}`} className={inputCls} />
              <input placeholder="Optional teaser link (Spotify preview, SoundCloud, etc.)" value={t.teaser_url || ""} onChange={(e) => updateTrack(idx, "teaser_url", e.target.value)} data-testid={`track-teaser-${idx}`} className={inputCls} />
            </div>
          ))}
          <button type="button" onClick={addTrack} data-testid="track-add" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add track</button>
        </div>
      </SectionCard>

      <SectionCard title="4. Follow / listen elsewhere">
        <Field label="Spotify URL">
          <input value={data.spotify_url || ""} onChange={(e) => set("spotify_url", e.target.value)} className={inputCls} placeholder="https://open.spotify.com/artist/…" />
        </Field>
        <Field label="Apple Music URL">
          <input value={data.apple_music_url || ""} onChange={(e) => set("apple_music_url", e.target.value)} className={inputCls} placeholder="https://music.apple.com/…" />
        </Field>
        <Field label="YouTube URL">
          <input value={data.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} className={inputCls} placeholder="https://youtube.com/@…" />
        </Field>
        <Field label="Instagram URL">
          <input value={data.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className={inputCls} placeholder="https://instagram.com/…" />
        </Field>
      </SectionCard>

      <SectionCard title="5. Subscribers">
        <p className="text-sm text-[#8A8A93]">Everyone who signed up from the /album email capture.</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => { setSubsOpen((v) => !v); if (!subsOpen) loadSubs(); }}
            data-testid="subs-toggle"
            className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors"
          >
            {subsOpen ? "Hide list" : "Load subscriber list"}
          </button>
          {subsOpen && subs.length > 0 && (
            <button type="button" onClick={downloadCsv} data-testid="subs-csv" className="overline text-[#C9A050] hover:text-white">Download CSV</button>
          )}
        </div>
        {subsOpen && (
          <div className="mt-3 border-t border-white/5">
            {subs.length === 0 && <p className="text-xs text-[#8A8A93] italic py-4">No subscribers yet.</p>}
            {subs.length > 0 && (
              <ul className="divide-y divide-white/5" data-testid="subs-list">
                {subs.map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm text-white">{s.email}</div>
                      <div className="text-xs text-[#8A8A93]">{s.name || "—"} · {s.source || "album"} · {(s.created_at || "").slice(0, 10)}</div>
                    </div>
                    <button type="button" onClick={() => removeSub(s.id)} className="overline text-[#8A8A93] hover:text-red-400 text-xs">Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="border border-white/5 p-6 md:p-7 space-y-5">
      <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#8A8A93] mt-2">{hint}</p>}
    </div>
  );
}
